import React, {ReactElement, useState} from 'react';
// TODO: INSTEAD OF USING MATERIAL UI COMPONENTS DIRECTLY, CREATE GENERIC COMPONENTS IN SYSTEM AND IMPORT IT
import {Card, CardContent, Divider, makeStyles, Link} from '@material-ui/core';
import {
  DEFAULT_WHITE_COLOR,
  APP_PRIMARY_COLOR
} from '../../../../../../style/variable';
import {useTranslation} from 'react-i18next';
import HelperUtils, {retryPaymentPlanFlag} from '@shared/utils/HelperUtils';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {PaymentPlanStatus} from '@shared/constants/Mapping';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import {retryPaymentPlanInstalmentLine} from '@shared/services/Payment.service';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';

const ActivePaymentPlanDetailsCardStyles = makeStyles(() => ({
  DetailsView: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1rem',
    overflowY: 'auto',
    maxHeight: '15.5rem'
  },
  activePaymentPlanMainView: {
    flexDirection: 'row',
    display: 'flex',
    backgroundColor: '#f4f6f8',
    padding: '0.8rem'
  },
  activePaymentPlanMainViews: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem'
  },
  activePaymentPlanHeaderValue: {
    color: '#343434',
    fontSize: '1rem',
    fontWeight: 700,
    paddingBlock: '1rem'
  },
  activePaymentPlanType: {
    width: '33%'
  },
  textOverFlow: {
    fontSize: '1rem',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    paddingRight: '0.2rem',
    textTransform: 'capitalize'
  },
  activePaymentPlanDetails: {
    width: '33%'
  },
  activePaymentPlanDetailsView: {
    width: '33%',
    display: 'flex',
    alignItems: 'center'
  },
  applyOddColor: {
    backgroundColor: 'rgb(0,0,0,0.05)'
  },
  applyEvenColor: {
    backgroundColor: DEFAULT_WHITE_COLOR
  },
  NoActivePaymentPlanInfoView: {
    flexDirection: 'row',
    display: 'flex'
  },
  divider: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  datePadding: {
    padding: '0.8rem'
  },
  displayFlex: {
    display: 'flex'
  }
}));

interface ActivePaymentPlanDetailsCardProps {
  activePlanInstalmentDetails: any;
  isConvenienceFeeExist?: any;
  showRetryInstalment: boolean;
  isGuarantorPortal: any;
  forceUpdateHandler(): void;
}
const ActivePaymentPlanDetailsCard = (
  props: ActivePaymentPlanDetailsCardProps
): ReactElement => {
  const classes = ActivePaymentPlanDetailsCardStyles();
  const {
    activePlanInstalmentDetails,
    isConvenienceFeeExist,
    showRetryInstalment,
    isGuarantorPortal,
    forceUpdateHandler
  } = props;
  const {t} = useTranslation();
  const [loaderId, setLoaderId] = useState<number>(0);
  const {enqueueSnackbar} = useSnackbar();

  const renderActivePaymentPlanHeaders = (): ReactElement => {
    return (
      <div className={classes.activePaymentPlanMainView}>
        <div
          className={[
            classes.activePaymentPlanDetailsView,
            classes.textOverFlow
          ].join(' ')}>
          <span className={classes.activePaymentPlanHeaderValue}>
            {t('PAYMENT_PLANS.PAYMENT_DATE')}
          </span>
        </div>
        <div
          className={[
            classes.activePaymentPlanDetailsView,
            classes.textOverFlow
          ].join(' ')}>
          <span className={classes.activePaymentPlanHeaderValue}>
            {t('PAYMENT_PLANS.PLAN_AMOUNT')}
          </span>
        </div>

        <div
          className={[
            classes.activePaymentPlanDetailsView,
            classes.textOverFlow
          ].join(' ')}>
          <span className={classes.activePaymentPlanHeaderValue}>
            {t('PAYMENT_PLANS.CHARGED_AMOUNT')}
          </span>
        </div>

        {!!isConvenienceFeeExist && (
          <div
            className={[
              classes.activePaymentPlanDetailsView,
              classes.textOverFlow
            ].join(' ')}>
            <span className={classes.activePaymentPlanHeaderValue}>
              {t('PAYMENT_PLANS.CONVENIENCE_FEE')}
            </span>
          </div>
        )}

        <div
          className={[
            classes.activePaymentPlanDetailsView,
            classes.textOverFlow
          ].join(' ')}>
          <span className={classes.activePaymentPlanHeaderValue}>
            {t('PAYMENT_PLANS.STATUS')}
          </span>
        </div>
      </div>
    );
  };

  const retryPaymentPlanLineFlag = (item: any) => {
    if (!item) {
      return false;
    }
    if (item?.status === PaymentPlanStatus.FAILED) {
      return true;
    } else if (
      item?.status === PaymentPlanStatus.PENDING &&
      retryPaymentPlanFlag(item?.instalment_date)
    ) {
      return true;
    }
    return false;
  };
  const settlePaymentPlanInstalmentLine = async (
    clinicGroupId: number,
    clinicId: number,
    paymentPlanInstalmentLineId: number
  ): Promise<void> => {
    try {
      setLoaderId(paymentPlanInstalmentLineId);
      const response = await retryPaymentPlanInstalmentLine(
        clinicGroupId,
        clinicId,
        paymentPlanInstalmentLineId
      );
      if (response && response?.message) {
        enqueueSnackbar(response?.message, SnackBarConfig('s'));
        forceUpdateHandler();
      }
    } catch (error: any) {
      enqueueSnackbar(error && error?.message, SnackBarConfig('e'));
    } finally {
      setLoaderId(0);
    }
  };

  const renderActivePaymentPlanData = (data: any): ReactElement => {
    return (
      <div>
        {data?.map((item: any, index: number) => {
          return (
            <div
              className={[
                classes.activePaymentPlanMainViews,
                classes.datePadding,
                index % 2 == 1 ? classes.applyEvenColor : classes.applyOddColor
              ].join(' ')}
              key={index}>
              <div
                className={[
                  classes.activePaymentPlanDetails,
                  classes.textOverFlow
                ].join(' ')}>
                {HelperUtils.formatDisplayDate(item?.instalment_date)}
              </div>
              <div
                className={[
                  classes.activePaymentPlanDetails,
                  classes.textOverFlow
                ].join(' ')}>
                <CurrencyFormatter
                  value={item?.instalment_amount}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  ignoreRound={true}
                  useSign={true}
                />
              </div>

              <div
                className={[
                  classes.activePaymentPlanDetails,
                  classes.textOverFlow
                ].join(' ')}>
                <CurrencyFormatter
                  value={item?.instalment_amount_paid}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  ignoreRound={true}
                  useSign={true}
                />
              </div>

              {!!isConvenienceFeeExist && (
                <div
                  className={[
                    classes.activePaymentPlanDetails,
                    classes.textOverFlow
                  ].join(' ')}>
                  <CurrencyFormatter
                    value={item?.convenience_fee || '-'}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                    ignoreRound={true}
                    useSign={true}
                  />
                </div>
              )}

              <div
                className={[
                  classes.activePaymentPlanDetails,
                  classes.textOverFlow,
                  classes.displayFlex
                ].join(' ')}>
                <span>{item?.status}</span>
                {!isGuarantorPortal &&
                  showRetryInstalment &&
                  retryPaymentPlanLineFlag(item) && (
                    <div style={{paddingLeft: '0.5rem'}}>
                      <Link
                        key={'retry_payment_plan_instalment_line'}
                        component="button"
                        onClick={(e: any): void => {
                          settlePaymentPlanInstalmentLine(
                            item?.clinic_group_id,
                            item?.clinic_id,
                            item?.id
                          );
                        }}>
                        {loaderId === item?.id ? (
                          <ButtonLoader colorCode={APP_PRIMARY_COLOR} />
                        ) : (
                          t('PAYMENT_PLANS.RETRY')
                        )}
                      </Link>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const renderActivePaymentPlanDetails = (): ReactElement => {
    return (
      <div>
        <Divider className={classes.divider} />
        {renderActivePaymentPlanData(activePlanInstalmentDetails)}
      </div>
    );
  };

  const renderNoActivePaymentPlanDetailsInfo = (): ReactElement => {
    return (
      <div className={classes.NoActivePaymentPlanInfoView}>
        <span className={classes.activePaymentPlanHeaderValue}>
          {t('PAYMENT_PLANS.NO_ACTIVE_INSTALMENT_DETAILS')}
        </span>
      </div>
    );
  };
  return (
    <Card className={classes.cardRoot}>
      <CardContent>
        <div>
          {activePlanInstalmentDetails?.length ? (
            [
              renderActivePaymentPlanHeaders(),
              <div className={classes.DetailsView} key={'render-plan-details'}>
                {renderActivePaymentPlanDetails()}
              </div>
            ]
          ) : (
            <div className={classes.DetailsView}>
              {renderNoActivePaymentPlanDetailsInfo()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
export default ActivePaymentPlanDetailsCard;
